import { PATH_DASHBOARD } from '../routes/paths';
import { STAGE_ICONS } from './stageIcons';

// Single source of truth for the "table" menu items that live under the
// Active Leads and Lead Management sidebar groups. NavConfig.jsx builds its
// children arrays from this, and the lead-detail / archive-detail breadcrumbs
// use menuCrumbsForOrigin() below to turn a stored `pageDetail` origin into
// "<menu group> › <menu label>" crumbs — so the sidebar and the breadcrumb
// can never drift out of sync.
//
// `groupPath` must match the `path` NavConfig gives the parent group item
// (Active Leads / Lead Management) — that's what the group crumb links to.
// `items` holds the literal { title, icon (stage icon name), path, feature? } list NavConfig renders
// as children (plus `{ divider: true }` placeholders); NavConfig applies its
// own hiddenNavTitles / hasMultiplePractices logic on top of this list.
export const TABLE_MENU_GROUPS = [
  {
    group: 'Active Leads',
    groupPath: PATH_DASHBOARD.tables?.newLeadTable,
    items: [
      { title: 'New', icon: STAGE_ICONS.NEW, path: PATH_DASHBOARD.tables?.newLeadTable },
      { title: 'Booked', icon: STAGE_ICONS.BOOKED, path: PATH_DASHBOARD.tables?.ConsultantBookedTable },
      { title: 'Attended', icon: STAGE_ICONS.ATTENDED, path: PATH_DASHBOARD.tables?.ConsultantAttendedTable },
      { title: 'CA Pipeline (New)', icon: STAGE_ICONS.CA_PIPELINE, path: PATH_DASHBOARD.tables?.ConsultantAttendedV2Table, feature: 'ca-pipeline-v2' },
      { title: 'Started', icon: STAGE_ICONS.STARTED, path: PATH_DASHBOARD.tables?.treatmentStartedTable },
      // NHS sits after Started, set apart by a hairline. The sidebar drops dividers that end up
      // leading / trailing / doubled when an item (e.g. NHS) is hidden — see tidyDividers.
      { divider: true },
      { title: 'NHS', icon: STAGE_ICONS.NHS, path: PATH_DASHBOARD.tables?.nhsLeadTable },
      { divider: true },
      { title: 'Aligner Approve', icon: STAGE_ICONS.ALIGNER_APPROVE, path: PATH_DASHBOARD.tables?.alignerApproveTable, feature: 'aligner-approve' },
    ],
  },
  {
    group: 'Lead Management',
    groupPath: PATH_DASHBOARD.tables?.archiveLeadTable,
    items: [
      { title: 'All', icon: STAGE_ICONS.ALL, path: PATH_DASHBOARD.tables?.AllLeadTable },
      { title: 'Archived', icon: STAGE_ICONS.ARCHIVED, path: PATH_DASHBOARD.tables?.archiveLeadTable },
      { title: 'Duplicate Leads', icon: STAGE_ICONS.DUPLICATE, path: PATH_DASHBOARD.tables?.duplicateLeadTable },
      { title: 'Spam', icon: STAGE_ICONS.SPAM, path: PATH_DASHBOARD.tables?.spamLeadTable },
      { divider: true },
      { title: 'No Task Assigned', icon: STAGE_ICONS.NO_TASK, path: PATH_DASHBOARD.tables?.NoTaskLeadsTable },
      { divider: true },
      { title: 'FTA', icon: STAGE_ICONS.FTA, path: PATH_DASHBOARD.tables?.failedToAttendTable },
      { title: 'Completed', icon: STAGE_ICONS.COMPLETED, path: PATH_DASHBOARD.tables?.closedTable },
      { title: 'All Patients', icon: STAGE_ICONS.ALL_PATIENTS, path: PATH_DASHBOARD.tables?.allPatients },
      // "All Practice Leads" is NOT listed here: it only exists in NavConfig
      // when hasMultiplePractices is true, so it stays inline there. An
      // origin on that path falls back to the stored pageDetail.name below.
    ],
  },
];

// Strip query string / hash so a stored pageDetail.path (which may carry
// deep-link filter params) can be compared against a bare menu item path.
const pathnameOf = (path) => (typeof path === 'string' ? path.split('?')[0].split('#')[0] : path);

// Turns a stored `state.lead.pageDetail` origin into the breadcrumb entries
// that go between the leading menu-group crumb and "Lead details":
//  - no path/name at all (deep-link, direct nav) → [] (no origin crumb)
//  - path matches a known menu item → [{ group }, { item }], item keeps the
//    FULL pageDetail.path (with query/hash) so its filters/deep-link params
//    survive the click-through
//  - anything else (WhatsApp's 'All Leads', notifications' 'New Leads', a
//    path that isn't a current menu item) → single fallback crumb using the
//    stored name, no group
export const menuCrumbsForOrigin = (pageDetail) => {
  if (!pageDetail || (!pageDetail.path && !pageDetail.name)) return [];

  const originPathname = pathnameOf(pageDetail.path);

  const matchedGroup = TABLE_MENU_GROUPS.reduce((found, candidateGroup) => {
    if (found) return found;
    const matchedItem = candidateGroup.items.find((item) => item.title && item.path === originPathname);
    return matchedItem ? { group: candidateGroup, item: matchedItem } : found;
  }, null);

  if (matchedGroup) {
    return [
      { name: matchedGroup.group.group, href: matchedGroup.group.groupPath },
      { name: matchedGroup.item.title, href: pageDetail.path },
    ];
  }

  return [{ name: pageDetail.name, href: pageDetail.path }];
};
